// Authoritative UK Clinical Symptom Index (NHS Pathways / NICE CKS / Bupa aligned)
// Maps patient presentation terms, symptoms, and conditions to canonical LIPS specialities.

export interface SymptomEntry {
  /** Canonical UK clinical/patient presentation term (e.g. "Chest Pain", "Sciatica") */
  symptom: string;
  /** Anatomical or clinical body system */
  category:
    | 'Cardiovascular & Chest'
    | 'Musculoskeletal & Joints'
    | 'Gastrointestinal & Digestive'
    | 'Ear, Nose & Throat (ENT)'
    | 'Dermatology & Skin'
    | 'Neurology & Brain'
    | 'Eyes & Vision'
    | 'Urology & Men’s Health'
    | 'Gynaecology & Women’s Health'
    | 'Respiratory & Lungs'
    | 'Mental Health'
    | 'General & Systemic';
  /** Alternative lay terms, colloquial names, and search keywords */
  synonyms: string[];
  /** Mapped LIPS speciality slugs (from SPEC_OPTIONS) */
  specialities: string[];
}

export const UK_SYMPTOMS: SymptomEntry[] = [
  // ── Cardiovascular & Chest ───────────────────────────────────────────────
  {
    symptom: 'Chest Pain',
    category: 'Cardiovascular & Chest',
    synonyms: [
      'angina',
      'chest tightness',
      'chest pressure',
      'heavy chest',
      'chest discomfort',
    ],
    specialities: ['cardiology', 'emergency-clinic-london'],
  },
  {
    symptom: 'Heart Palpitations',
    category: 'Cardiovascular & Chest',
    synonyms: [
      'palpitations',
      'irregular heartbeat',
      'fluttering heart',
      'racing heart',
      'pounding heart',
      'arrhythmia',
      'skipped heartbeats',
    ],
    specialities: ['cardiology', 'private-gp'],
  },
  {
    symptom: 'High Blood Pressure',
    category: 'Cardiovascular & Chest',
    synonyms: [
      'hypertension',
      'elevated blood pressure',
      'bp check',
      'high bp',
    ],
    specialities: ['cardiology', 'private-gp'],
  },
  {
    symptom: 'Shortness of Breath',
    category: 'Cardiovascular & Chest',
    synonyms: [
      'breathlessness',
      'dyspnoea',
      'cant breathe',
      'gasping',
      'winded',
      'wheezing',
      'shallow breathing',
    ],
    specialities: ['cardiology', 'respiratory-medicine', 'private-gp'],
  },
  {
    symptom: 'Swollen Ankles & Legs',
    category: 'Cardiovascular & Chest',
    synonyms: [
      'oedema',
      'edema',
      'leg swelling',
      'fluid retention',
      'puffy feet',
    ],
    specialities: ['cardiology', 'general-surgery', 'private-gp'],
  },

  // ── Musculoskeletal & Joints ─────────────────────────────────────────────
  {
    symptom: 'Knee Pain',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'swollen knee',
      'torn meniscus',
      'knee clicking',
      'knee arthritis',
      'runner knee',
      'knee stiffness',
      'arthrosamid',
    ],
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'arthrosamid',
      'physiotherapy',
    ],
  },
  {
    symptom: 'Hip Pain',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'groin pain',
      'hip stiffness',
      'hip arthritis',
      'clicking hip',
      'hip clicking',
      'bursitis',
    ],
    specialities: [
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    symptom: 'Back Pain & Sciatica',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'sciatica',
      'back ache',
      'lower back pain',
      'slipped disc',
      'herniated disc',
      'lumbago',
      'shooting leg pain',
      'spinal pain',
    ],
    specialities: ['neurosurgery', 'physiotherapy'],
  },
  {
    symptom: 'Neck Pain & Stiffness',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'stiff neck',
      'cervical pain',
      'whiplash',
      'pinched nerve neck',
      'trapped nerve',
    ],
    specialities: ['neurosurgery', 'physiotherapy'],
  },
  {
    symptom: 'Joint Pain & Arthritis',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'arthritis',
      'osteoarthritis',
      'rheumatoid arthritis',
      'swollen joints',
      'joint stiffness',
      'gout',
    ],
    specialities: [
      'rheumatology',
      'hip-replacement-and-knee-replacement',
      'trauma-and-orthopaedics-lower-limb',
      'physiotherapy',
    ],
  },
  {
    symptom: 'Shoulder Pain',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'frozen shoulder',
      'rotator cuff',
      'shoulder impingement',
      'shoulder clicking',
      'shoulder stiffness',
    ],
    specialities: ['trauma-and-orthopaedics-upper-limb', 'physiotherapy'],
  },
  {
    symptom: 'Hand & Wrist Pain',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'carpal tunnel',
      'trigger finger',
      'wrist sprain',
      'hand stiffness',
      'numb fingers',
      'dupuytren',
    ],
    specialities: [
      'trauma-and-orthopaedics-upper-limb',
      'plastic-surgery',
      'physiotherapy',
    ],
  },
  {
    symptom: 'Foot & Ankle Pain',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'bunions',
      'plantar fasciitis',
      'ankle sprain',
      'achilles tendonitis',
      'flat feet',
      'heel pain',
    ],
    specialities: ['trauma-and-orthopaedics-lower-limb', 'physiotherapy'],
  },
  {
    symptom: 'Osteoporosis & Bone Density',
    category: 'Musculoskeletal & Joints',
    synonyms: [
      'bone density',
      'dexa scan',
      'bone scan',
      'weak bones',
      'brittle bones',
      'osteopenia',
    ],
    specialities: ['bone-scan', 'rheumatology', 'private-gp'],
  },

  // ── Gastrointestinal & Digestive ─────────────────────────────────────────
  {
    symptom: 'Acid Reflux & Heartburn',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'heartburn',
      'acid indigestion',
      'gerd',
      'gord',
      'burning chest',
      'sour taste',
      'waterbrash',
    ],
    specialities: ['general-surgery', 'gastric-alimetry', 'private-gp'],
  },
  {
    symptom: 'Abdominal Pain & Stomach Ache',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'stomach cramps',
      'belly ache',
      'tummy pain',
      'gut pain',
      'cramping',
      'gastric pain',
      'ibs',
    ],
    specialities: [
      'general-surgery',
      'gut-health-test',
      'gastric-alimetry',
      'dietetics',
    ],
  },
  {
    symptom: 'Bloating & Indigestion',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'bloated stomach',
      'distension',
      'excess gas',
      'flatulence',
      'dyspepsia',
      'gut discomfort',
    ],
    specialities: [
      'gut-health-test',
      'dietetics',
      'gastric-alimetry',
      'general-surgery',
    ],
  },
  {
    symptom: 'Bowel Changes & Diarrhoea',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'diarrhoea',
      'loose stools',
      'watery bowel',
      'constipation',
      'irregular bowels',
      'bowel habit change',
    ],
    specialities: ['general-surgery', 'gut-health-test', 'dietetics'],
  },
  {
    symptom: 'Haemorrhoids & Piles',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'piles',
      'rectal bleeding',
      'bleeding bottom',
      'anal itching',
      'exroid',
      'anal lump',
      'anal fissure',
    ],
    specialities: ['exroid', 'general-surgery'],
  },
  {
    symptom: 'Hernia',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'groin lump',
      'inguinal hernia',
      'umbilical hernia',
      'hiatus hernia',
      'abdominal bulge',
      'hernia repair',
    ],
    specialities: ['general-surgery'],
  },
  {
    symptom: 'Liver Health & Jaundice',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'fatty liver',
      'liver scan',
      'fibroscan',
      'jaundice',
      'yellow skin',
      'elevated liver enzymes',
    ],
    specialities: ['liver-health-liver-scans', 'general-surgery', 'blood-test'],
  },
  {
    symptom: 'Difficulty Swallowing',
    category: 'Gastrointestinal & Digestive',
    synonyms: [
      'dysphagia',
      'food sticking',
      'choking sensation',
      'globus',
      'lump in throat',
    ],
    specialities: ['ent', 'general-surgery', 'gastric-alimetry'],
  },

  // ── Ear, Nose & Throat (ENT) & Hearing ───────────────────────────────────
  {
    symptom: 'Tinnitus & Ear Ringing',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'ringing in ears',
      'buzzing ears',
      'pulsatile tinnitus',
      'ear hissing',
      'whooshing sound',
    ],
    specialities: [
      'audiology-hearing-test-battersea-london',
      'ent',
      'hearing-aids-london',
    ],
  },
  {
    symptom: 'Hearing Loss & Blocked Ears',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'deafness',
      'hard of hearing',
      'ear wax',
      'clogged ears',
      'muffled hearing',
      'hearing test',
    ],
    specialities: [
      'audiology-hearing-test-battersea-london',
      'hearing-aids-london',
      'ent',
    ],
  },
  {
    symptom: 'Earache & Ear Discharge',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'ear pain',
      'ear infection',
      'glue ear',
      'swimmers ear',
      'leaking ear',
      'perforated eardrum',
    ],
    specialities: ['ent', 'private-gp'],
  },
  {
    symptom: 'Sinusitis & Blocked Nose',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'sinus pain',
      'nasal congestion',
      'nasal polyps',
      'rhinitis',
      'deviated septum',
      'stuffy nose',
      'loss of smell',
    ],
    specialities: ['ent', 'private-gp'],
  },
  {
    symptom: 'Sore Throat & Tonsillitis',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'tonsil stones',
      'strep throat',
      'swollen tonsils',
      'hoarse voice',
      'laryngitis',
      'voice loss',
    ],
    specialities: ['ent', 'private-gp'],
  },
  {
    symptom: 'Sleep Apnoea & Snoring',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'snoring',
      'sleep apnea',
      'choking in sleep',
      'daytime sleepiness',
      'stop breathing at night',
      'osa',
    ],
    specialities: ['obstructive-sleep-apnoea', 'ent', 'respiratory-medicine'],
  },
  {
    symptom: 'Jaw Pain & Clicking',
    category: 'Ear, Nose & Throat (ENT)',
    synonyms: [
      'tmj',
      'jaw clicking',
      'locked jaw',
      'teeth grinding',
      'bruxism',
      'temporomandibular',
    ],
    specialities: ['maxillofacial', 'dental-services', 'ent'],
  },

  // ── Dermatology & Skin ───────────────────────────────────────────────────
  {
    symptom: 'Moles & Skin Lesions',
    category: 'Dermatology & Skin',
    synonyms: [
      'mole check',
      'changing mole',
      'skin check',
      'melanoma check',
      'mole mapping',
      'freckle check',
      'suspicious mole',
    ],
    specialities: ['mole-mapping', 'dermatology', 'plastic-surgery'],
  },
  {
    symptom: 'Lumps, Bumps & Cysts',
    category: 'Dermatology & Skin',
    synonyms: [
      'lipoma',
      'sebaceous cyst',
      'skin tag',
      'ganglion',
      'subcutaneous lump',
      'epidermoid cyst',
      'fatty lump',
    ],
    specialities: [
      'lumps-and-bumps',
      'general-surgery',
      'plastic-surgery',
      'dermatology',
    ],
  },
  {
    symptom: 'Eczema & Dermatitis',
    category: 'Dermatology & Skin',
    synonyms: [
      'dry skin',
      'itchy rash',
      'contact dermatitis',
      'atopic eczema',
      'flaky skin',
      'inflamed skin',
    ],
    specialities: ['dermatology', 'private-gp'],
  },
  {
    symptom: 'Psoriasis',
    category: 'Dermatology & Skin',
    synonyms: [
      'scaly skin',
      'silvery plaques',
      'scalp psoriasis',
      'psoriatic skin',
      'plaque psoriasis',
    ],
    specialities: ['dermatology', 'rheumatology'],
  },
  {
    symptom: 'Acne & Rosacea',
    category: 'Dermatology & Skin',
    synonyms: [
      'spots',
      'pimples',
      'facial redness',
      'cystic acne',
      'breakouts',
      'flushed cheeks',
    ],
    specialities: ['dermatology', 'aesthetics-clinic', 'private-gp'],
  },
  {
    symptom: 'Hair Loss & Alopecia',
    category: 'Dermatology & Skin',
    synonyms: [
      'alopecia',
      'thinning hair',
      'bald patches',
      'hair shedding',
      'scalp loss',
    ],
    specialities: ['dermatology', 'blood-test', 'private-gp'],
  },

  // ── Neurology & Brain ────────────────────────────────────────────────────
  {
    symptom: 'Headaches & Migraines',
    category: 'Neurology & Brain',
    synonyms: [
      'migraine',
      'cluster headache',
      'tension headache',
      'throbbing head',
      'ocular migraine',
      'head pressure',
    ],
    specialities: ['neurology', 'neurosurgery', 'private-gp'],
  },
  {
    symptom: 'Dizziness & Vertigo',
    category: 'Neurology & Brain',
    synonyms: [
      'spinning sensation',
      'bppv',
      'loss of balance',
      'unsteadiness',
      'lightheadedness',
      'fainting',
      'syncope',
    ],
    specialities: [
      'neurology',
      'audiology-hearing-test-battersea-london',
      'ent',
      'cardiology',
    ],
  },
  {
    symptom: 'Tremors & Shaking',
    category: 'Neurology & Brain',
    synonyms: [
      'parkinsonism',
      'essential tremor',
      'hand tremor',
      'involuntary movements',
      'twitching',
    ],
    specialities: ['neurology', 'geriatrics'],
  },
  {
    symptom: 'Numbness, Tingling & Neuropathy',
    category: 'Neurology & Brain',
    synonyms: [
      'pins and needles',
      'paresthesia',
      'nerve pain',
      'peripheral neuropathy',
      'loss of sensation',
      'burning feet',
    ],
    specialities: ['neurology', 'neurosurgery', 'rheumatology'],
  },
  {
    symptom: 'Memory Loss & Cognitive Decline',
    category: 'Neurology & Brain',
    synonyms: [
      'dementia',
      'brain fog',
      'forgetfulness',
      'confusion',
      'alzheimer check',
      'cognitive assessment',
    ],
    specialities: ['neurology', 'geriatrics', 'psychiatry'],
  },
  {
    symptom: 'Seizures & Epilepsy',
    category: 'Neurology & Brain',
    synonyms: [
      'fits',
      'convulsions',
      'epileptic fit',
      'absence seizures',
      'blackouts',
    ],
    specialities: ['neurology'],
  },

  // ── Eyes & Vision ────────────────────────────────────────────────────────
  {
    symptom: 'Blurry Vision & Vision Loss',
    category: 'Eyes & Vision',
    synonyms: [
      'cloudy vision',
      'dim vision',
      'failing eyesight',
      'reduced vision',
      'vision deterioration',
    ],
    specialities: ['ophthalmology'],
  },
  {
    symptom: 'Cataracts',
    category: 'Eyes & Vision',
    synonyms: [
      'cataract',
      'misty vision',
      'glare at night',
      'lens clouding',
      'cataract assessment',
    ],
    specialities: ['ophthalmology'],
  },
  {
    symptom: 'Eye Pain & Redness',
    category: 'Eyes & Vision',
    synonyms: [
      'red eye',
      'conjunctivitis',
      'gritty eyes',
      'dry eyes',
      'eye irritation',
      'inflamed eye',
      'stye',
    ],
    specialities: ['ophthalmology', 'private-gp'],
  },
  {
    symptom: 'Glaucoma & Eye Pressure',
    category: 'Eyes & Vision',
    synonyms: [
      'high eye pressure',
      'tunnel vision',
      'glaucoma test',
      'optic nerve check',
    ],
    specialities: ['ophthalmology'],
  },
  {
    symptom: 'Eye Floaters & Flashes',
    category: 'Eyes & Vision',
    synonyms: [
      'floaters',
      'flashing lights',
      'retinal tear',
      'cobwebs in vision',
      'vitreous detachment',
    ],
    specialities: ['ophthalmology'],
  },

  // ── Urology & Men’s Health ───────────────────────────────────────────────
  {
    symptom: 'Urine Infection & Painful Urination',
    category: 'Urology & Men’s Health',
    synonyms: [
      'uti',
      'cystitis',
      'dysuria',
      'burning pee',
      'cloudy urine',
      'blood in urine',
      'haematuria',
    ],
    specialities: ['urology-and-andrology', 'sexual-health', 'private-gp'],
  },
  {
    symptom: 'Prostate Issues & Frequent Urination',
    category: 'Urology & Men’s Health',
    synonyms: [
      'bph',
      'enlarged prostate',
      'weak urine stream',
      'peeing at night',
      'nocturia',
      'psa check',
      'prostate check',
    ],
    specialities: ['urology-and-andrology', 'blood-test', 'private-gp'],
  },
  {
    symptom: 'Erectile Dysfunction',
    category: 'Urology & Men’s Health',
    synonyms: [
      'ed',
      'impotence',
      'low libido',
      'loss of erection',
      'sexual dysfunction',
    ],
    specialities: [
      'urology-and-andrology',
      'trt',
      'sexual-health',
      'private-gp',
    ],
  },
  {
    symptom: 'Low Testosterone & TRT',
    category: 'Urology & Men’s Health',
    synonyms: [
      'low t',
      'trt assessment',
      'testosterone replacement',
      'hypogonadism',
      'male fatigue',
      'loss of muscle',
    ],
    specialities: ['trt', 'urology-and-andrology', 'blood-test'],
  },
  {
    symptom: 'Testicular Pain & Lumps',
    category: 'Urology & Men’s Health',
    synonyms: [
      'testicle lump',
      'scrotal pain',
      'hydrocele',
      'varicocele',
      'epididymitis',
      'swollen testicle',
    ],
    specialities: ['urology-and-andrology', 'ultrasound'],
  },
  {
    symptom: 'STI & Sexual Health Screening',
    category: 'Urology & Men’s Health',
    synonyms: [
      'std',
      'sti check',
      'chlamydia',
      'gonorrhoea',
      'hiv test',
      'genital discharge',
      'genital warts',
    ],
    specialities: ['sexual-health', 'urology-and-andrology', 'blood-test'],
  },

  // ── Gynaecology & Women’s Health ─────────────────────────────────────────
  {
    symptom: 'Menopause & Hot Flushes',
    category: 'Gynaecology & Women’s Health',
    synonyms: [
      'hot flashes',
      'perimenopause',
      'hrt',
      'night sweats',
      'mood changes',
      'vaginal dryness',
      'menopause check',
    ],
    specialities: [
      'menopause-clinic',
      'gynaecology-and-obstetrics',
      'blood-test',
    ],
  },
  {
    symptom: 'Heavy & Irregular Periods',
    category: 'Gynaecology & Women’s Health',
    synonyms: [
      'menorrhagia',
      'painful periods',
      'cramps',
      'missed periods',
      'fibroids',
      'endometriosis',
      'pcos',
    ],
    specialities: ['gynaecology-and-obstetrics', 'ultrasound', 'blood-test'],
  },
  {
    symptom: 'Pelvic Pain & Ovarian Cysts',
    category: 'Gynaecology & Women’s Health',
    synonyms: [
      'pelvic ache',
      'ovarian cyst',
      'pain during intercourse',
      'dyspareunia',
      'adenomyosis',
    ],
    specialities: ['gynaecology-and-obstetrics', 'ultrasound'],
  },
  {
    symptom: 'Fertility & Conception',
    category: 'Gynaecology & Women’s Health',
    synonyms: [
      'female fertility',
      'cant get pregnant',
      'infertility',
      'egg reserve',
      'amh check',
      'fertility mot',
    ],
    specialities: [
      'female-fertility',
      'gynaecology-and-obstetrics',
      'ultrasound',
    ],
  },
  {
    symptom: 'Breast Lumps & Breast Pain',
    category: 'Gynaecology & Women’s Health',
    synonyms: [
      'breast lump',
      'breast check',
      'nipple discharge',
      'mastalgia',
      'breast cyst',
      'mammogram',
    ],
    specialities: ['breast-clinic', 'ultrasound', 'radiology'],
  },

  // ── Mental Health & Wellbeing ────────────────────────────────────────────
  {
    symptom: 'Anxiety & Panic Attacks',
    category: 'Mental Health',
    synonyms: [
      'panic',
      'nervousness',
      'phobia',
      'generalized anxiety',
      'gad',
      'racing thoughts',
      'social anxiety',
    ],
    specialities: ['psychiatry', 'private-gp'],
  },
  {
    symptom: 'Depression & Low Mood',
    category: 'Mental Health',
    synonyms: [
      'feeling sad',
      'loss of interest',
      'anhedonia',
      'major depression',
      'grief',
      'mood swings',
      'hopelessness',
    ],
    specialities: ['psychiatry', 'private-gp'],
  },
  {
    symptom: 'ADHD Assessment & Focus Issues',
    category: 'Mental Health',
    synonyms: [
      'adhd',
      'add',
      'attention deficit',
      'hyperactivity',
      'poor concentration',
      'executive dysfunction',
    ],
    specialities: ['psychiatry', 'childrens-health-paediatrics'],
  },
  {
    symptom: 'Children & Teen Mental Health',
    category: 'Mental Health',
    synonyms: [
      'child anxiety',
      'adolescent psychiatry',
      'school refusal',
      'child adhd',
      'autism check',
    ],
    specialities: ['childrens-health-paediatrics', 'psychiatry'],
  },

  // ── General & Systemic ───────────────────────────────────────────────────
  {
    symptom: 'Chronic Fatigue & Tiredness',
    category: 'General & Systemic',
    synonyms: [
      'exhaustion',
      'always tired',
      'lethargy',
      'low energy',
      'me cfs',
      'burnout',
    ],
    specialities: ['private-gp', 'blood-test', 'dietetics', 'haematology'],
  },
  {
    symptom: 'Unexplained Weight Changes',
    category: 'General & Systemic',
    synonyms: [
      'weight loss',
      'weight gain',
      'rapid weight change',
      'metabolism check',
      'thyroid check',
    ],
    specialities: ['dietetics', 'blood-test', 'private-gp', 'general-surgery'],
  },
  {
    symptom: 'Blood Disorders & Anaemia',
    category: 'General & Systemic',
    synonyms: [
      'anaemia',
      'iron deficiency',
      'bruising',
      'low platelets',
      'blood count',
      'clotting',
    ],
    specialities: ['haematology', 'blood-test', 'private-gp'],
  },
  {
    symptom: 'Elderly Care & Frailty',
    category: 'General & Systemic',
    synonyms: [
      'geriatric care',
      'falls in elderly',
      'frailty',
      'mobility decline',
      'elderly assessment',
    ],
    specialities: ['geriatrics', 'private-gp', 'physiotherapy'],
  },
  {
    symptom: 'Urgent Cuts, Wounds & Minor Injuries',
    category: 'General & Systemic',
    synonyms: [
      'minor injury',
      'cut',
      'sprain',
      'fracture',
      'burn',
      'wound care',
      'walk in clinic',
      'urgent care',
    ],
    specialities: ['emergency-clinic-london', 'private-gp'],
  },
];

/**
 * Searches the UK symptoms registry for entries whose symptom title, category,
 * or synonyms match the given query string.
 */
export function searchSymptoms(query: string): SymptomEntry[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length < 2) return [];

  return UK_SYMPTOMS.filter((item) => {
    if (item.symptom.toLowerCase().includes(trimmed)) return true;
    if (item.category.toLowerCase().includes(trimmed)) return true;
    return item.synonyms.some(
      (syn) =>
        syn.toLowerCase().includes(trimmed) ||
        trimmed.includes(syn.toLowerCase()),
    );
  });
}

/**
 * Returns all unique LIPS speciality slugs mapped to symptoms matching the query.
 */
export function getSpecialitiesForQuerySymptoms(query: string): string[] {
  const matched = searchSymptoms(query);
  const slugs = new Set<string>();
  matched.forEach((item) => {
    item.specialities.forEach((slug) => slugs.add(slug));
  });
  return Array.from(slugs);
}
